import { useState } from 'react'

import { Link, LinkProps } from '@tanstack/react-router'

import { useAuth } from '@/services/auth/use-auth'

import { Logo, SiteHeaderActionUser } from '@/components/layout/site-header'
import { Button } from '@/components/ui/button'

import { cn } from '@/lib/cn'

// Types
interface User {
  id: string | number
  name: string
  email: string
}

interface MobileMenuProps {
  isMobileMenuOpen: boolean
  setIsMobileMenuOpen: (open: boolean) => void
  user: User | null
  currentPath: string
  HeaderMenu: React.ComponentType<any>
  HeaderNavList: React.ComponentType<any>
  SiteHeaderActionUser: React.ComponentType<any>
  Button: React.ComponentType<any>
}

interface HeaderMenuProps {
  isMobile?: boolean
  setIsMobileMenuOpen?: (open: boolean) => void
}

interface HeaderNavListProps {
  isMobile?: boolean
  setIsMobileMenuOpen?: (open: boolean) => void
}
interface NavBarProps {
  /**
   * Navbar className
   * @default 'bg-transparent absolute top-0 z-40 flex h-[55px] min-h-16 w-full items-center justify-between px-4 md:px-32'
   */
  className?: string
}
interface NavBarProps {
  /**
   * Navbar className
   * @default 'bg-transparent absolute top-0 z-40 flex h-[55px] min-h-16 w-full items-center justify-between px-4 md:px-32'
   */
  className?: string
}

export default function Navbar({ className }: NavBarProps) {
  const user = useAuth((s) => s.user)
  const currentPath = window.location.pathname
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <div
      className={cn(
        'absolute top-0 z-40 flex h-[55px] min-h-16 w-full items-center justify-between bg-transparent px-4 md:px-32',
        className
      )}
    >
      <>
        <div className="flex items-center gap-2">
          <Logo />
          {/* Desktop HeaderMenu */}
          <HeaderMenu isMobile={false} />

          {user?.id ? (
            <>
              <HeaderNavList isMobile={false} />
            </>
          ) : (
            <></>
          )}
        </div>

        {/* Mobile Menu Toggle and Overlay */}
        <MobileMenuToggleAndOverlay
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          user={user}
          currentPath={currentPath}
          HeaderMenu={HeaderMenu}
          HeaderNavList={HeaderNavList}
          SiteHeaderActionUser={SiteHeaderActionUser}
          Button={Button}
        />
      </>
      {/* Desktop Actions - Hidden on mobile */}
      <div className="hidden md:flex">
        {user?.id ? (
          <>
            <SiteHeaderActionUser
              user={{
                name: user.name,
                email: user.email,
              }}
            />
          </>
        ) : (
          <>
            <div className="flex gap-4">
              <Link
                to="/auth/login"
                search={{
                  redirectTo: currentPath,
                }}
              >
                <Button variant={'transparant'}>Masuk</Button>
              </Link>
              <Link to="/auth/register">
                <Button variant={'primary'}>Daftar</Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function HeaderNavList({
  isMobile = false,
  setIsMobileMenuOpen,
}: HeaderNavListProps) {
  const NavList = [
    {
      label: 'Dashboard',
      to: '/dashboard',
    },
    {
      label: 'Profile',
      to: '/profile',
    },
  ] satisfies Array<{ label: string; to: LinkProps['to'] }>

  if (isMobile) {
    return (
      <div className="w-full">
        <ul className="flex flex-col items-center gap-4 text-center">
          {NavList.map((item, index) => (
            <li key={index}>
              <Link
                to={item.to}
                className="text-lg font-medium text-white transition-colors hover:text-gray-300"
                onClick={() =>
                  setIsMobileMenuOpen && setIsMobileMenuOpen(false)
                }
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <div className="hidden md:flex">
      <span className="font-medium text-primary-yes"> | </span>
      <ul className="ml-4 flex items-center gap-2 text-center text-primary-yes md:gap-6">
        {NavList.map((item, index) => (
          <li key={index}>
            <Link className="font-medium" to={item.to}>
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

function HeaderMenu({
  isMobile = false,
  setIsMobileMenuOpen,
}: HeaderMenuProps) {
  const [isProgramDropdownOpen, setIsProgramDropdownOpen] = useState(false)

  const NavList = [
    {
      label: 'Home',
      to: '/',
    },
    {
      label: 'Tentang Kami',
      to: '/about-us',
    },
    {
      label: 'Program',
      isDropdown: true,
      dropdownItems: [
        {
          label: 'GIjinkoku Preparation',
          to: '/program/gijinkoku-preparation',
          target: '_self',
        },
        {
          label: '5 Days Basic Nihonggo Challange',
          to: 'https://challenge.yes.co.id/',
          target: '_blank',
        },
        {
          label: 'N5 Preparation',
          to: 'https://n5prep.yes.co.id/',
          target: '_blank',
        },
      ],
    },
    {
      label: 'Karir',
      to: '/career-page',
    },
  ]

  // Mobile version
  if (isMobile) {
    return (
      <div className="w-full">
        <ul className="flex flex-col items-center gap-6 text-center">
          {NavList.map((item, index) => (
            <li key={index} className="relative w-full">
              {item.isDropdown ? (
                <div className="w-full">
                  <button
                    onClick={() =>
                      setIsProgramDropdownOpen(!isProgramDropdownOpen)
                    }
                    className="flex w-full items-center justify-center gap-2 text-lg font-medium text-primary-yes transition-colors hover:text-gray-300 focus:outline-none"
                  >
                    {item.label}
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 transition-transform duration-200 ${isProgramDropdownOpen ? 'rotate-180' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isProgramDropdownOpen && (
                    <div className="mt-4 w-full">
                      {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                        <div key={dropdownIndex} className="mb-3">
                          <Link
                            to={dropdownItem.to}
                            className="block text-base font-medium text-gray-300 transition-colors hover:text-white"
                            onClick={() => {
                              setIsProgramDropdownOpen(false)
                              setIsMobileMenuOpen && setIsMobileMenuOpen(false)
                            }}
                            target={dropdownItem.target}
                          >
                            {dropdownItem.label}
                          </Link>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.to}
                  className="text-lg font-medium text-white transition-colors hover:text-gray-300"
                  onClick={() =>
                    setIsMobileMenuOpen && setIsMobileMenuOpen(false)
                  }
                >
                  {item.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  // Desktop version (unchanged)
  return (
    <div className="hidden items-center gap-2 md:flex">
      <ul className="flex items-center gap-2 text-center md:gap-6">
        {NavList.map((item, index) => (
          <li key={index} className="relative">
            {item.isDropdown ? (
              <>
                <button
                  onClick={() =>
                    setIsProgramDropdownOpen(!isProgramDropdownOpen)
                  }
                  className="flex items-center gap-1 font-medium text-primary-yes focus:outline-none"
                >
                  {item.label}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform duration-200 ${isProgramDropdownOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {isProgramDropdownOpen && (
                  <ul className="absolute left-0 top-full z-50 mt-2 w-[350px] rounded-md bg-primary-yes py-2 shadow-lg">
                    {item.dropdownItems.map((dropdownItem, dropdownIndex) => (
                      <li key={dropdownIndex}>
                        <Link
                          to={dropdownItem.to}
                          className="block px-5 py-4 text-left font-medium text-white hover:bg-yes-500"
                          onClick={() => setIsProgramDropdownOpen(false)}
                          target={dropdownItem.target}
                        >
                          {dropdownItem.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <Link to={item.to} className="font-medium text-primary-yes">
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function MobileMenuToggleAndOverlay({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  user,
  currentPath,
  HeaderMenu,
  HeaderNavList,
  SiteHeaderActionUser,
  Button,
}: MobileMenuProps) {
  return (
    <>
      {/* Tombol hamburger untuk mobile */}
      <div className="flex items-center md:hidden">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="font-weight-bold p-2 text-primary-yes focus:outline-none"
          aria-label="Toggle mobile menu"
        >
          <svg
            className="h-6 w-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isMobileMenuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-30 bg-black bg-opacity-95 md:hidden">
          {/* Close button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute right-4 top-4 z-50 p-2 text-3xl text-white transition-colors hover:text-gray-300 focus:outline-none"
            aria-label="Close mobile menu"
          >
            &times;
          </button>

          {/* Menu Content */}
          <div className="flex min-h-screen flex-col items-center justify-center space-y-8 px-6 py-20">
            {/* Main Navigation */}
            <div className="w-full max-w-sm">
              <HeaderMenu
                isMobile={true}
                setIsMobileMenuOpen={setIsMobileMenuOpen}
              />
            </div>

            {/* User Navigation (jika user login) */}
            {user?.id && (
              <div className="w-full max-w-sm">
                <div className="mb-6 ml-4 border-t border-gray-600 pt-6">
                  <HeaderNavList
                    isMobile={true}
                    setIsMobileMenuOpen={setIsMobileMenuOpen}
                  />
                </div>
              </div>
            )}

            {/* User Actions */}
            <div className="w-full max-w-sm">
              {user?.id ? (
                <div className="flex flex-col items-center">
                  <div className="rounded-lg border border-gray-600 bg-black bg-opacity-50 p-4">
                    <SiteHeaderActionUser
                      user={{
                        name: user.name,
                        email: user.email,
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex w-full flex-col gap-4">
                  <Link
                    to="/auth/login"
                    search={{
                      redirectTo: currentPath,
                    }}
                  >
                    <Button
                      variant={'transparant'}
                      className="w-full"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Masuk
                    </Button>
                  </Link>
                  <Link to="/auth/register">
                    <Button
                      variant={'primary'}
                      className="w-full"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Daftar
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
